<script lang="ts" module>
  // ProgressBar — the design-system determinate/indeterminate bar
  // (wiki/feature-design-system.md §Component primitives, AC4). One bar for
  // every "bytes are moving" surface: the post-take keep-upload (SaveToLibrary)
  // and the post-take AI-Review upload (UploadPanel) render THIS, never their
  // own copy of the markup and keyframes.
  //
  // `percent` null = the length isn't computable yet, so the fill runs as an
  // indeterminate stripe instead of claiming a number it doesn't have.
  //
  // Forwards arbitrary attributes (`data-testid`, `class`, …) onto the root via
  // `...rest`, so a host never wraps it just to attach a test hook.
</script>

<script lang="ts">
  import type { HTMLAttributes } from 'svelte/elements';

  interface Props extends HTMLAttributes<HTMLSpanElement> {
    /** 0–100, or null while the total length is unknown. */
    percent: number | null;
    /** Accessible name — what is being uploaded. */
    label: string;
  }

  let { percent, label, ...rest }: Props = $props();
</script>

<span
  class="vd-progress"
  role="progressbar"
  aria-valuemin="0"
  aria-valuemax="100"
  aria-valuenow={percent ?? undefined}
  aria-label={label}
  {...rest}
>
  <span
    class="vd-progress-fill"
    class:indeterminate={percent == null}
    style={percent == null ? '' : `width: ${percent}%`}
  ></span>
</span>

<style lang="scss">
  .vd-progress {
    position: relative;
    display: block;
    width: 8rem;
    height: 0.55rem;
    border-radius: 999px;
    background: color-mix(in srgb, var(--accent) 14%, transparent);
    overflow: hidden;
  }

  .vd-progress-fill {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: var(--accent);
    transition: width 0.15s ease;
  }

  .vd-progress-fill.indeterminate {
    width: 40%;
    animation: vd-progress-indeterminate 1.1s ease-in-out infinite;
  }

  @keyframes vd-progress-indeterminate {
    0% {
      margin-left: -40%;
    }
    100% {
      margin-left: 100%;
    }
  }
</style>
